import { useEffect, useState } from "react";
import { RefreshCw, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMobileNetworkRecoveryState } from "@shared/mobileNetworkRecovery";

export function MobileNetworkRecoveryBanner() {
  const [online, setOnline] = useState(() => typeof navigator === "undefined" || navigator.onLine);
  const [rechecking, setRechecking] = useState(false);
  const [recentlyRestored, setRecentlyRestored] = useState(false);

  useEffect(() => {
    let clearRestored: number | undefined;
    const handleOffline = () => {
      setOnline(false);
      setRecentlyRestored(false);
      setRechecking(false);
    };
    const handleOnline = () => {
      setOnline(true);
      setRechecking(false);
      setRecentlyRestored(true);
      clearRestored = window.setTimeout(() => setRecentlyRestored(false), 8_000);
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      if (clearRestored) window.clearTimeout(clearRestored);
    };
  }, []);

  const retryConnection = async () => {
    setRechecking(true);
    try {
      const response = await fetch("/", { method: "HEAD", cache: "no-store" });
      if (!response.ok) throw new Error("Resposta indisponível");
      window.dispatchEvent(new Event("online"));
    } catch {
      setOnline(false);
      setRechecking(false);
    }
  };

  const state = getMobileNetworkRecoveryState({ online, rechecking, recentlyRestored });
  if (!state) return null;

  const isDanger = state.tone === "danger";
  const isSuccess = state.tone === "success";
  const Icon = isDanger ? WifiOff : isSuccess ? Wifi : RefreshCw;
  return (
    <div className={`border-b px-3 py-2 md:hidden ${isDanger ? "border-red-200 bg-red-50 text-red-950" : isSuccess ? "border-emerald-200 bg-emerald-50 text-emerald-950" : "border-amber-200 bg-amber-50 text-amber-950"}`} role="status" aria-live="polite">
      <div className="mx-auto flex max-w-3xl items-center gap-2.5">
        <Icon className={`h-4 w-4 shrink-0 ${rechecking ? "animate-spin" : ""}`} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold">{state.title}</p>
          <p className="text-[11px] leading-snug opacity-85">{state.description}</p>
        </div>
        {isDanger && (
          <Button size="sm" variant="outline" className="h-9 shrink-0 border-current bg-transparent text-xs" onClick={retryConnection} disabled={rechecking}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${rechecking ? "animate-spin" : ""}`} />
            Verificar
          </Button>
        )}
      </div>
    </div>
  );
}
